import {
  type Icon,
  type IconWeight,
  VideoCameraIcon,
  HeadphonesIcon,
  SpeakerHighIcon,
  ChatCircleIcon,
  ChatTeardropDotsIcon,
  SparkleIcon,
  QuestionIcon,
  CardsIcon,
  ArticleIcon,
  NotePencilIcon,
  BookmarkIcon,
  SealQuestionIcon,
  HighlighterIcon,
  PenNibIcon,
  ClipboardTextIcon,
  GearSixIcon,
  FileTextIcon,
} from "@phosphor-icons/react";
import type { AddOnType } from "pages/media/types";

/** Prototype-only add-ons with no backend `AddOnType` yet (lowercase = UI
 *  only). They render in the catalog/rail but never reach an API payload. */
export const UI_ONLY_ADDON_IDS = [
  "notes",
  "faqs",
  "heygenius",
  "forms",
  "esignature",
] as const;

export type UiOnlyAddonId = (typeof UI_ONLY_ADDON_IDS)[number];

/** Add-on ids ARE the backend `AddOnType` enum values (codegen keeps them in
 *  sync — a backend rename fails the build here), plus the UI-only
 *  prototypes. `"General"` is excluded: it's the API type behind the fixed
 *  "general" settings card, not an addable rail add-on. */
export type AddonId = Exclude<AddOnType, "General"> | UiOnlyAddonId;

/**
 * Fixed "Settings" section entries (not add-ons).
 * `chat` stays in the union for `CONFIG_ITEM_VISUALS`, but the rail lists chat
 * as an add-on row — it is one, and it has to be removable there.
 */
export type SettingsItemId = "general" | "documents" | "chat";

/** Any rail item — a settings entry or an add-on. Mirrors `ConfigItemId` in
 *  `config-cards/registry.ts`, restated here so this data module doesn't have
 *  to import the component layer. */
export type ConfigItemVisualId = SettingsItemId | AddonId;

/**
 * Named constants for every rail item id, so components reference
 * `CONFIG_ITEM.Summary` instead of the bare `"Summary"` string.
 *
 * Keys are PascalCase regardless of the id's own casing (the backend
 * `AddOnType` values are capitalized, the UI-only and settings ids are not),
 * so callers don't have to remember which is which.
 *
 * `as const` (not a type annotation) is what types each value as its own
 * literal, so `CONFIG_ITEM.Summary` stays assignable to `AddonId`. The
 * `satisfies` clause only adds the checks: keys must be the capitalized id and
 * a new `AddOnType` (or settings entry) fails to compile until it is listed.
 */
export const CONFIG_ITEM = {
  // Settings
  General: "general",
  Documents: "documents",
  Chat: "chat",
  // Add-ons
  Video: "Video",
  Podcast: "Podcast",
  AudioDoc: "AudioDoc",
  Heygenius: "heygenius",
  Quiz: "Quiz",
  Flashcards: "Flashcards",
  Summary: "Summary",
  Notes: "notes",
  Bookmarks: "Bookmarks",
  Trivia: "Trivia",
  Standouts: "Standouts",
  Faqs: "faqs",
  Chatbot: "Chatbot",
  Forms: "forms",
  Esignature: "esignature",
} as const satisfies { [K in ConfigItemVisualId as Capitalize<K>]: K };

/** Grounded AI chat. Named because it is the only add-on whose enabled state
 *  is mirrored outside `enabled_add_ons` (the instance's `chat_mode`). */
export const CHAT_ADDON_ID = CONFIG_ITEM.Chatbot;

export interface ConfigItemVisual {
  Icon: Icon;
  /** Accent hex. The chip tints its background to ~12% of it. */
  color: string;
  weight: IconWeight;
}

/**
 * The glyph + accent for every rail item, shared by the rail chip, the "Add a
 * new add-on" tiles and the config-card header so they can't drift apart.
 *
 * Exhaustive over `ConfigItemVisualId` on purpose: it covers the add-ons that
 * are still commented out of `ADDON_CATALOG` for v1, so re-enabling one keeps
 * its icon instead of silently falling back to a gear. A new backend
 * `AddOnType` fails to compile until it gets a visual here.
 *
 * Weight follows the mock: settings entries are filled, add-ons duotone.
 */
export const CONFIG_ITEM_VISUALS: Record<ConfigItemVisualId, ConfigItemVisual> =
  {
    // Settings
    general: { Icon: GearSixIcon, color: "#6B7280", weight: "fill" },
    documents: { Icon: FileTextIcon, color: "#3B82F6", weight: "fill" },
    chat: { Icon: ChatCircleIcon, color: "#2FAAA2", weight: "fill" },
    // Add-ons
    Video: { Icon: VideoCameraIcon, color: "#14B8A6", weight: "duotone" },
    Podcast: { Icon: HeadphonesIcon, color: "#10B981", weight: "duotone" },
    AudioDoc: { Icon: SpeakerHighIcon, color: "#0EA5E9", weight: "duotone" },
    heygenius: { Icon: SparkleIcon, color: "#8B5CF6", weight: "duotone" },
    Quiz: { Icon: QuestionIcon, color: "#6366F1", weight: "duotone" },
    Flashcards: { Icon: CardsIcon, color: "#3B82F6", weight: "duotone" },
    Summary: { Icon: ArticleIcon, color: "#F59E0B", weight: "duotone" },
    notes: { Icon: NotePencilIcon, color: "#84A700", weight: "duotone" },
    Bookmarks: { Icon: BookmarkIcon, color: "#8B5CF6", weight: "duotone" },
    Trivia: { Icon: SealQuestionIcon, color: "#EC4899", weight: "duotone" },
    Standouts: { Icon: HighlighterIcon, color: "#14B8A6", weight: "duotone" },
    faqs: { Icon: ChatCircleIcon, color: "#F43F5E", weight: "duotone" },
    Chatbot: {
      Icon: ChatTeardropDotsIcon,
      color: "#14B8A6",
      weight: "duotone",
    },
    forms: { Icon: ClipboardTextIcon, color: "#F97316", weight: "duotone" },
    esignature: { Icon: PenNibIcon, color: "#059669", weight: "duotone" },
  };

export const getConfigItemVisual = (id: ConfigItemVisualId): ConfigItemVisual =>
  CONFIG_ITEM_VISUALS[id];

/** Add-on category, used to group the "Add a new add-on" modal. Group headers
 *  render in catalog order (first add-on of each group wins). */
export type AddonGroup = "media" | "knowledgeHub" | "engagement";

export interface AddonDef {
  id: AddonId;
  /** i18n key for the display name (resolved in the component). */
  labelKey: string;
  /** i18n key for the one-line description (shown in the add modal). */
  descriptionKey: string;
  group: AddonGroup;
  /**
   * Approximate credit cost, shown as "~N cr". PLACEHOLDER pricing carried over
   * from the prototype — the backend owns the real numbers and charges them at
   * creation time. Source these from the API before relying on them (a stale
   * value here would contradict the actual charge). Tracked with the phase-2
   * add-on wiring.
   */
  credits: number;
}

/**
 * Catalog of add-ons available in the customize step. This is the pool the
 * "Add a new add-on" button picks from; the left panel shows the subset a
 * playbook has enabled. Names are i18n keys (resolved in the components) so
 * nothing user-facing is hardcoded. Icons/accents come from
 * `CONFIG_ITEM_VISUALS`, keyed by `id`.
 *
 * NOTE: this is the working set carried over from the design prototype. The
 * finalized per-playbook add-on sets (Lean Academic, Full Academic, HeyGenius +
 * Chat, Video + Chat) and user-created playbooks are still to be defined —
 * until then a customize session starts from `BUILD_YOUR_OWN_ADDONS`.
 */
export const ADDON_CATALOG: AddonDef[] = [
  {
    id: CONFIG_ITEM.Video,
    labelKey: "createExperience.addons.video",
    descriptionKey: "createExperience.addonDescriptions.video",
    group: "media",
    credits: 800,
  },
  {
    id: CONFIG_ITEM.Podcast,
    labelKey: "createExperience.addons.audio",
    descriptionKey: "createExperience.addonDescriptions.audio",
    group: "media",
    credits: 120,
  },
  // TODO(MED-7032): AudioDoc hidden for now — uncomment to re-enable (also in config-cards/registry.ts).
  // { id: CONFIG_ITEM.AudioDoc, labelKey: "createExperience.addons.audiodoc", descriptionKey: "createExperience.addonDescriptions.audiodoc", group: "media", credits: 160 },
  // TODO(MED-6966): commented out for v1, not wired on the BE yet — uncomment once it is.
  // { id: CONFIG_ITEM.Heygenius, labelKey: "createExperience.addons.heygenius", descriptionKey: "createExperience.addonDescriptions.heygenius", group: "media", credits: 800 },
  {
    id: CONFIG_ITEM.Quiz,
    labelKey: "createExperience.addons.quiz",
    descriptionKey: "createExperience.addonDescriptions.quiz",
    group: "knowledgeHub",
    credits: 80,
  },
  {
    id: CONFIG_ITEM.Flashcards,
    labelKey: "createExperience.addons.quickRecall",
    descriptionKey: "createExperience.addonDescriptions.quickRecall",
    group: "knowledgeHub",
    credits: 90,
  },
  {
    id: CONFIG_ITEM.Summary,
    labelKey: "createExperience.addons.keyPoints",
    descriptionKey: "createExperience.addonDescriptions.keyPoints",
    group: "knowledgeHub",
    credits: 50,
  },
  // TODO(MED-6966): commented out for v1, not wired on the BE yet — uncomment once it is.
  // { id: CONFIG_ITEM.Notes, labelKey: "createExperience.addons.notes", descriptionKey: "createExperience.addonDescriptions.notes", group: "knowledgeHub", credits: 20 },
  // { id: CONFIG_ITEM.Bookmarks, labelKey: "createExperience.addons.bookmarks", descriptionKey: "createExperience.addonDescriptions.bookmarks", group: "knowledgeHub", credits: 20 },
  // { id: CONFIG_ITEM.Trivia, labelKey: "createExperience.addons.trivia", descriptionKey: "createExperience.addonDescriptions.trivia", group: "knowledgeHub", credits: 60 },
  {
    id: CONFIG_ITEM.Standouts,
    labelKey: "createExperience.addons.standouts",
    descriptionKey: "createExperience.addonDescriptions.standouts",
    group: "knowledgeHub",
    credits: 40,
  },
  // TODO(MED-6966): commented out for v1, not wired on the BE yet — uncomment once it is.
  // { id: CONFIG_ITEM.Faqs, labelKey: "createExperience.addons.faqs", descriptionKey: "createExperience.addonDescriptions.faqs", group: "knowledgeHub", credits: 70 },
  {
    id: CONFIG_ITEM.Chatbot,
    labelKey: "createExperience.addons.aichat",
    descriptionKey: "createExperience.addonDescriptions.aichat",
    group: "engagement",
    credits: 200,
  },
  // TODO(MED-6966): commented out for v1, not wired on the BE yet — uncomment once it is.
  // { id: CONFIG_ITEM.Forms, labelKey: "createExperience.addons.forms", descriptionKey: "createExperience.addonDescriptions.forms", group: "engagement", credits: 35 },
  // { id: CONFIG_ITEM.Esignature, labelKey: "createExperience.addons.esignature", descriptionKey: "createExperience.addonDescriptions.esignature", group: "engagement", credits: 30 },
];

export interface SettingsItemDef {
  id: SettingsItemId;
  labelKey: string;
}

/** Settings rail for the create flow (#customize). */
export const SETTINGS_ITEMS: SettingsItemDef[] = [
  { id: CONFIG_ITEM.General, labelKey: "createExperience.customize.general" },
  {
    id: CONFIG_ITEM.Documents,
    labelKey: "createExperience.customize.documents",
  },
];

/**
 * Settings rail for the edit page. Same entries as the create flow; kept as a
 * named export so the edit page and rail don't depend on the create-flow name.
 */
export const EDIT_SETTINGS_ITEMS: SettingsItemDef[] = SETTINGS_ITEMS;

/**
 * Starting add-ons for a customize session. "Build your own" begins with just
 * grounded AI chat; this is also the current default for playbook-seeded
 * sessions until per-playbook add-on sets are defined.
 */
export const BUILD_YOUR_OWN_ADDONS: AddonId[] = [CHAT_ADDON_ID];
